/**
 * SoleadLab 2.0 — Score Panel (D2.4)
 *
 * Floating overlay panel displaying scoring results.
 * All displayed values are verbatim backend outputs — no frontend derivation.
 *
 * Three-state model:
 *   scored     → total_score + confidence_score + component breakdown
 *   not_scored → "Not scored" with blocked_reason
 *   no_state   → "No state snapshot" message
 *
 * Per contract §15: never display 0 or N/A as a substitute for an actual score.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScoringStore } from '@/stores/scoringStore';
import { useTruthStore } from '@/stores/truthStore';
import { useProjectStore } from '@/stores/projectStore';
import { useIsReadOnlyViewer } from '@/hooks/useShareViewer';
import type {
  ScoreEntry,
  ScoredEntry,
  NotScoredEntry,
  NoStateEntry,
  BreakdownJSON,
  BreakdownComponent,
  IneligibleComponent,
  ScoreComponentsResponse,
} from '@/types/scoring';
import { SCORE_TYPE_LABELS } from '@/types/scoring';
import type { SiteTruthRow, TruthExplanation, TruthMissing, ConclusionFamily } from '@/types/truth';
import { ELIGIBILITY_LABELS, ELIGIBILITY_COLORS, CONCLUSION_FAMILY_LABELS } from '@/types/truth';
import '@/styles/scoring.css';

interface ScorePanelProps {
  onClose: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatScore(score: number): string {
  return (score * 100).toFixed(1) + '%';
}

function scoreLevel(score: number): 'high' | 'medium' | 'low' {
  if (score >= 0.7) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
}

function confidenceLevel(score: number): 'high' | 'medium' | 'low' {
  if (score >= 0.7) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
}

function formatTs(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function friendlyBlockedReason(reason: string): string {
  const map: Record<string, string> = {
    no_state_snapshot: 'No state snapshot available',
    state_confidence_below_threshold: 'State confidence too low',
    quality_below_threshold: 'Data quality below threshold',
    invalid_entity_score_type: 'Invalid entity/score type combination',
    unsupported_quality_dimension: 'Required quality dimensions not available',
    insufficient_component_coverage: 'Insufficient component coverage',
    no_score_row: 'Score not yet computed',
  };
  return map[reason] || reason;
}

// ─── Score Card (three-state) ───────────────────────────────────────────────

function ScoreCard({
  scoreType,
  entry,
  onDrilldown,
}: {
  scoreType: string;
  entry: ScoreEntry;
  onDrilldown?: (entry: ScoredEntry) => void;
}) {
  const label = SCORE_TYPE_LABELS[scoreType] || scoreType;

  if (entry.status === 'no_state') {
    const e = entry as NoStateEntry;
    return (
      <div className="score-card score-card--no-state">
        <div className="score-card__header">
          <span className="score-card__label">{label}</span>
          <span className="score-card__status-pill score-card__status-pill--no-state">No State</span>
        </div>
        <div className="score-card__message">{e.message}</div>
      </div>
    );
  }

  if (entry.status === 'not_scored') {
    const e = entry as NotScoredEntry;
    return (
      <div className="score-card score-card--not-scored">
        <div className="score-card__header">
          <span className="score-card__label">{label}</span>
          <span className="score-card__status-pill score-card__status-pill--not-scored">Not Scored</span>
        </div>
        <div className="score-card__blocked-reason">{friendlyBlockedReason(e.blocked_reason)}</div>
        <div className="score-card__blocked-detail">{e.blocked_detail}</div>
      </div>
    );
  }

  // scored
  const e = entry as ScoredEntry;
  const level = scoreLevel(e.total_score);
  const confLevel = confidenceLevel(e.confidence_score);

  return (
    <div
      className="score-card score-card--scored"
      onClick={() => onDrilldown?.(e)}
      title="Click for breakdown"
    >
      <div className="score-card__header">
        <span className="score-card__label">{label}</span>
        <span className="score-card__ts">{formatTs(e.created_at)}</span>
      </div>
      <div className="score-card__scores-row">
        <div className="score-card__total">
          <span className={`score-card__total-value score-card__total-value--${level}`}>
            {formatScore(e.total_score)}
          </span>
          <span className="score-card__total-label">Score</span>
        </div>
        <div className="score-card__confidence">
          <div className="score-card__confidence-bar">
            <div
              className={`score-card__confidence-fill score-card__confidence-fill--${confLevel}`}
              style={{ width: `${Math.round(e.confidence_score * 100)}%` }}
            />
          </div>
          <span className="score-card__confidence-label">
            {formatScore(e.confidence_score)} confidence
          </span>
        </div>
      </div>
      <div className="score-card__drilldown-hint">Click for component breakdown</div>
    </div>
  );
}

// ─── Breakdown View ─────────────────────────────────────────────────────────

function BreakdownView({
  entry,
  components: dbComponents,
  componentsLoading,
  onBack,
}: {
  entry: ScoredEntry;
  components: ScoreComponentsResponse | null;
  componentsLoading: boolean;
  onBack: () => void;
}) {
  const breakdown = entry.breakdown_json as BreakdownJSON;
  const label = SCORE_TYPE_LABELS[entry.score_type] || entry.score_type;

  return (
    <div className="score-breakdown">
      <button className="score-breakdown__back" onClick={onBack} type="button">
        ← Back to overview
      </button>

      <div className="score-breakdown__header">
        <h3 className="score-breakdown__title">{label}</h3>
        <div className="score-breakdown__score-row">
          <span className={`score-breakdown__total score-breakdown__total--${scoreLevel(entry.total_score)}`}>
            {formatScore(entry.total_score)}
          </span>
          <span className="score-breakdown__conf">
            {formatScore(entry.confidence_score)} confidence
          </span>
        </div>
      </div>

      {/* Confidence derivation */}
      <div className="score-breakdown__section">
        <div className="score-breakdown__section-title">Confidence Derivation</div>
        <div className="score-breakdown__derivation">
          <div className="score-breakdown__derivation-row">
            <span>Data Quality Score</span>
            <span>{breakdown.confidence_derivation.data_quality_score.toFixed(4)}</span>
          </div>
          <div className="score-breakdown__derivation-row">
            <span>Component Coverage</span>
            <span>{breakdown.confidence_derivation.component_coverage_fraction.toFixed(4)}</span>
          </div>
          <div className="score-breakdown__derivation-row score-breakdown__derivation-row--result">
            <span>{breakdown.confidence_derivation.formula}</span>
            <span>{breakdown.confidence_derivation.result.toFixed(4)}</span>
          </div>
        </div>
      </div>

      {/* Eligible components */}
      {breakdown.components.length > 0 && (
        <div className="score-breakdown__section">
          <div className="score-breakdown__section-title">
            Eligible Components ({breakdown.components.length})
          </div>
          <div className="score-breakdown__components">
            {breakdown.components.map((c: BreakdownComponent) => (
              <ComponentCard key={c.score_component_id} component={c} />
            ))}
          </div>
        </div>
      )}

      {/* Ineligible components */}
      {breakdown.ineligible_components.length > 0 && (
        <div className="score-breakdown__section">
          <div className="score-breakdown__section-title">
            Ineligible Components ({breakdown.ineligible_components.length})
          </div>
          <div className="score-breakdown__ineligible">
            {breakdown.ineligible_components.map((c: IneligibleComponent) => (
              <div key={c.component_type} className="score-breakdown__ineligible-item">
                <span className="score-breakdown__ineligible-type">{c.component_type}</span>
                <span className="score-breakdown__ineligible-reason">{c.reason}</span>
                <span className="score-breakdown__ineligible-weight">weight: {c.base_weight}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Score verification */}
      <div className="score-breakdown__section">
        <div className="score-breakdown__section-title">Score Verification</div>
        <div className="score-breakdown__derivation">
          <div className="score-breakdown__derivation-row">
            <span>Sum of contributions</span>
            <span>{breakdown.total_score_verification.sum_of_contributions.toFixed(4)}</span>
          </div>
          <div className="score-breakdown__derivation-row">
            <span>Sum of effective weights</span>
            <span>{breakdown.total_score_verification.sum_of_effective_weights.toFixed(4)}</span>
          </div>
          <div className="score-breakdown__derivation-row score-breakdown__derivation-row--result">
            <span>Total score</span>
            <span>{breakdown.total_score_verification.total_score.toFixed(4)}</span>
          </div>
        </div>
      </div>

      {/* DB components if loaded */}
      {componentsLoading && (
        <div className="score-breakdown__loading">Loading DB components…</div>
      )}
      {dbComponents && !componentsLoading && (
        <div className="score-breakdown__section">
          <div className="score-breakdown__section-title">
            DB Component Records ({dbComponents.components.length})
          </div>
          <div className="score-breakdown__db-components">
            {dbComponents.components.map((c) => (
              <div key={c.id} className="score-breakdown__db-row">
                <span className="score-breakdown__db-type">{c.component_type}</span>
                <span>raw={c.raw_value?.toFixed(4) ?? 'null'}</span>
                <span>norm={c.normalized_value?.toFixed(4) ?? 'null'}</span>
                <span>w={c.weight.toFixed(4)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ComponentCard({ component: c }: { component: BreakdownComponent }) {
  const pct = (c.effective_weight * 100).toFixed(1);
  return (
    <div className="score-component-card">
      <div className="score-component-card__header">
        <span className="score-component-card__type">{c.component_type}</span>
        <span className="score-component-card__contribution">
          +{c.weighted_contribution.toFixed(4)}
        </span>
      </div>
      <div className="score-component-card__details">
        <div className="score-component-card__bar-row">
          <div className="score-component-card__bar">
            <div
              className={`score-component-card__bar-fill score-component-card__bar-fill--${scoreLevel(c.normalized_value)}`}
              style={{ width: `${Math.round(c.normalized_value * 100)}%` }}
            />
          </div>
          <span>{(c.normalized_value * 100).toFixed(1)}%</span>
        </div>
        <div className="score-component-card__meta">
          raw: {c.raw_value?.toFixed(4) ?? 'n/a'} · weight: {c.base_weight} → {c.effective_weight.toFixed(4)} ({pct}%)
        </div>
      </div>
    </div>
  );
}

// ─── Truth Qualification Card ────────────────────────────────────────────────

/** Map score_type to the truth family that feeds it */
const SCORE_TYPE_TO_TRUTH_FAMILY: Record<string, ConclusionFamily> = {
  site_health: 'condition',
  site_risk: 'risk',
};

function TruthQualificationCard({ truth, onRecompute, isRecomputing, isReadOnly = false }: {
  truth: SiteTruthRow | null;
  onRecompute: () => void;
  isRecomputing: boolean;
  /** F-02E-2B: when true, the Recompute / Run-Truth-Qualification
      buttons are hidden. Defaults to false so authenticated callers
      pre-F-02E-2B see no behaviour change. */
  isReadOnly?: boolean;
}) {
  if (!truth) {
    return (
      <div className="truth-card truth-card--empty">
        <div className="truth-card__title">Truth Qualification</div>
        <div className="truth-card__message">
          {isReadOnly
            ? 'Truth qualification has not been computed for this project.'
            : 'Not yet computed. Run truth qualification to provide completeness/coverage scores.'}
        </div>
        {!isReadOnly && (
          <button
            className="state-btn state-btn--primary state-btn--sm"
            onClick={onRecompute}
            disabled={isRecomputing}
            type="button"
            data-testid="truth-card-run"
          >
            {isRecomputing ? 'Computing...' : 'Run Truth Qualification'}
          </button>
        )}
      </div>
    );
  }

  let explanation: TruthExplanation | null = null;
  let missing: TruthMissing[] = [];
  try { explanation = JSON.parse(truth.explanation_json); } catch { /* */ }
  try { missing = JSON.parse(truth.missing_json); } catch { /* */ }

  const eligColor = ELIGIBILITY_COLORS[truth.eligibility_state];
  const familyLabel = CONCLUSION_FAMILY_LABELS[truth.conclusion_family];

  return (
    <div className="truth-card">
      <div className="truth-card__header">
        <span className="truth-card__title">Truth: {familyLabel}</span>
        <span
          className="truth-card__eligibility"
          style={{ background: eligColor, color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}
        >
          {ELIGIBILITY_LABELS[truth.eligibility_state]}
        </span>
      </div>

      {/* Score bars */}
      <div className="truth-card__scores">
        <div className="truth-card__score-row">
          <span className="truth-card__score-label">Completeness</span>
          <div className="truth-card__bar">
            <div className="truth-card__bar-fill" style={{ width: `${Math.round(truth.completeness_score * 100)}%`, background: '#3b82f6' }} />
          </div>
          <span className="truth-card__score-value">{(truth.completeness_score * 100).toFixed(1)}%</span>
        </div>
        <div className="truth-card__score-row">
          <span className="truth-card__score-label">Coverage</span>
          <div className="truth-card__bar">
            <div className="truth-card__bar-fill" style={{ width: `${Math.round(truth.coverage_score * 100)}%`, background: '#8b5cf6' }} />
          </div>
          <span className="truth-card__score-value">{(truth.coverage_score * 100).toFixed(1)}%</span>
        </div>
        <div className="truth-card__score-row">
          <span className="truth-card__score-label">Confidence</span>
          <div className="truth-card__bar">
            <div className="truth-card__bar-fill" style={{ width: `${Math.round(truth.confidence_score * 100)}%`, background: '#10b981' }} />
          </div>
          <span className="truth-card__score-value">{(truth.confidence_score * 100).toFixed(1)}%</span>
        </div>
      </div>

      {/* Evidence & freshness */}
      <div className="truth-card__meta">
        <span>Evidence: {truth.direct_evidence_status}</span>
        <span>Freshness: {truth.freshness_status}</span>
        {truth.multimodality_ok === 0 && <span style={{ color: '#dc2626' }}>Multimodality insufficient</span>}
      </div>

      {/* Explanation summary */}
      {explanation?.summary && (
        <div className="truth-card__summary">{explanation.summary}</div>
      )}

      {/* Missing evidence */}
      {missing.length > 0 && (
        <div className="truth-card__missing">
          <div className="truth-card__missing-title">Missing Evidence</div>
          {missing.slice(0, 5).map((m, i) => (
            <div key={i} className="truth-card__missing-item">
              {m.count} {m.asset_type}(s) need {m.family} — {m.suggestion}
            </div>
          ))}
          {missing.length > 5 && (
            <div className="truth-card__missing-more">+{missing.length - 5} more</div>
          )}
        </div>
      )}

      {/* Next actions */}
      {explanation?.next_actions && explanation.next_actions.length > 0 && (
        <div className="truth-card__actions">
          <div className="truth-card__actions-title">Next Actions</div>
          {explanation.next_actions.slice(0, 3).map((a, i) => (
            <div key={i} className="truth-card__action-item">{a}</div>
          ))}
        </div>
      )}

      {!isReadOnly && (
        <button
          className="state-btn state-btn--secondary state-btn--sm"
          onClick={onRecompute}
          disabled={isRecomputing}
          type="button"
          style={{ marginTop: 8 }}
          data-testid="truth-card-recompute"
        >
          {isRecomputing ? 'Recomputing...' : 'Recompute'}
        </button>
      )}
    </div>
  );
}

// ─── Main Panel ─────────────────────────────────────────────────────────────

export default function ScorePanel({ onClose }: ScorePanelProps) {
  // F-02E-2B: share viewers see the score read-out (latest / history /
  // components are all shareReadable when 'scores' is exposed) but no
  // Compute (POST /scores/compute) button. Truth Recompute is also a
  // mutation — passed through `onRecompute={undefined}` to suppress.
  const isReadOnly = useIsReadOnlyViewer();
  const siteId = useProjectStore((s) => s.meta.siteId);

  const setSiteId = useScoringStore((s) => s.setSiteId);
  const siteScores = useScoringStore((s) => s.siteScores);
  const assetScoresList = useScoringStore((s) => s.assetScoresList);
  const loading = useScoringStore((s) => s.loading);
  const error = useScoringStore((s) => s.error);
  const loadScores = useScoringStore((s) => s.loadScores);
  const loadComponents = useScoringStore((s) => s.loadComponents);
  const clearComponents = useScoringStore((s) => s.clearComponents);
  const computeScores = useScoringStore((s) => s.computeScores);
  const computeStatus = useScoringStore((s) => s.computeStatus);
  const components = useScoringStore((s) => s.components);
  const componentsLoading = useScoringStore((s) => s.componentsLoading);

  // Truth qualification store
  const setTruthSiteId = useTruthStore((s) => s.setSiteId);
  const truthByFamily = useTruthStore((s) => s.truthByFamily);
  const truthRecompute = useTruthStore((s) => s.recompute);
  const truthRecomputeStatus = useTruthStore((s) => s.recomputeStatus);

  const [tab, setTab] = useState<'site' | 'assets'>('site');
  const [drilldownEntry, setDrilldownEntry] = useState<ScoredEntry | null>(null);

  useEffect(() => {
    setSiteId(siteId ?? null);
    setTruthSiteId(siteId ?? null);
  }, [setSiteId, setTruthSiteId, siteId]);

  const handleDrilldown = (entry: ScoredEntry) => {
    setDrilldownEntry(entry);
    loadComponents(entry.score_snapshot_id);
  };

  const handleBackFromDrilldown = () => {
    setDrilldownEntry(null);
    clearComponents();
  };

  const isComputing = computeStatus === 'running';

  const siteScoreEntries = Object.entries(siteScores);
  const hasAnyScores = siteScoreEntries.length > 0 || assetScoresList.length > 0;

  const panel = (
    <div className="score-panel-overlay" onClick={onClose}>
      <div className="score-panel-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="score-panel-header">
          <div className="score-panel-header__left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20V10"/>
              <path d="M18 20V4"/>
              <path d="M6 20v-4"/>
            </svg>
            Scores
          </div>
          <div className="score-panel-header__actions">
            {!isReadOnly && (
              <button
                className="state-btn state-btn--primary state-btn--sm"
                disabled={isComputing || !siteId}
                onClick={computeScores}
                type="button"
                data-testid="score-panel-compute"
              >
                {isComputing ? 'Computing…' : '↺ Compute'}
              </button>
            )}
            <button
              className="state-btn state-btn--secondary state-btn--sm"
              onClick={loadScores}
              disabled={loading}
              type="button"
            >
              Refresh
            </button>
            <button className="score-panel-close" onClick={onClose} type="button">×</button>
          </div>
        </div>

        {/* Drilldown view */}
        {drilldownEntry ? (
          <div className="score-panel-body">
            <BreakdownView
              entry={drilldownEntry}
              components={components}
              componentsLoading={componentsLoading}
              onBack={handleBackFromDrilldown}
            />
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="score-panel-tabs">
              <button
                className={`state-panel-tab${tab === 'site' ? ' state-panel-tab--active' : ''}`}
                onClick={() => setTab('site')}
                type="button"
              >
                Site Scores
              </button>
              <button
                className={`state-panel-tab${tab === 'assets' ? ' state-panel-tab--active' : ''}`}
                onClick={() => setTab('assets')}
                type="button"
              >
                Asset Scores ({assetScoresList.length})
              </button>
            </div>

            {/* Body */}
            <div className="score-panel-body">
              {!siteId && (
                <div className="score-panel-empty">
                  Save project to a site to enable scoring.
                </div>
              )}

              {siteId && loading && (
                <div className="score-panel-loading">Loading scores…</div>
              )}

              {siteId && error && !loading && (
                <div className="score-panel-error">{error}</div>
              )}

              {siteId && !loading && !error && !hasAnyScores && (
                <div className="score-panel-empty">
                  No scores computed yet. Click Compute to generate scores.
                </div>
              )}

              {siteId && !loading && !error && hasAnyScores && tab === 'site' && (
                <div className="score-panel-cards">
                  {siteScoreEntries.map(([scoreType, entry]) => {
                    const truthFamily = SCORE_TYPE_TO_TRUTH_FAMILY[scoreType];
                    const showTruthCard = entry.status === 'not_scored' && truthFamily;
                    return (
                      <div key={scoreType}>
                        <ScoreCard
                          scoreType={scoreType}
                          entry={entry}
                          onDrilldown={handleDrilldown}
                        />
                        {showTruthCard && (
                          <TruthQualificationCard
                            truth={truthByFamily[truthFamily]}
                            /* F-02E-2B: no-op in share mode so the
                               Recompute button is inert. Hiding the
                               button entirely would require gating
                               inside TruthQualificationCard which is
                               local to this file — see below. */
                            onRecompute={isReadOnly ? () => {} : () => truthRecompute(truthFamily)}
                            isRecomputing={truthRecomputeStatus === 'running'}
                            isReadOnly={isReadOnly}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {siteId && !loading && !error && hasAnyScores && tab === 'assets' && (
                <div className="score-panel-asset-list">
                  {assetScoresList.length === 0 && (
                    <div className="score-panel-empty">No assets with state snapshots found.</div>
                  )}
                  {assetScoresList.map(({ asset_id, scores }) => (
                    <div key={asset_id} className="score-panel-asset-group">
                      <div className="score-panel-asset-group__id">{asset_id}</div>
                      <div className="score-panel-cards">
                        {Object.entries(scores).map(([scoreType, entry]) => (
                          <ScoreCard
                            key={scoreType}
                            scoreType={scoreType}
                            entry={entry}
                            onDrilldown={handleDrilldown}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {computeStatus === 'done' && (
                <div className="score-panel-compute-done">
                  Scores computed successfully. Refreshing…
                </div>
              )}
              {computeStatus === 'error' && (
                <div className="score-panel-compute-error">
                  Score computation failed. Check console for details.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
